import React, { useState, useRef, useCallback, useEffect } from 'react'
import { colors } from './fieldTheme'
import { isMine } from './fieldSummary'
import { formatDateTime, nowSeconds } from './fieldDates'
import { CameraIcon, GalleryIcon, ClearIcon, CheckIcon, DotsIcon, PencilIcon, TrashIcon } from './FieldIcons'
import { ActionSheet, BottomSheet, SheetGroup, SheetRow, PrimaryButton, SecondaryButton, ErrorNote } from './FieldUI'
import { preparePhotos, DuplicateNote } from './FieldPhotoPicker'
import FieldComments, { UndoToast } from './FieldComments'

// Volets Photo et Commentaire (canvas W19b0, W19b, W19c, W20 et W21, décision 48) : deux actions
// séparées, les mêmes volets depuis un thème et après l'enregistrement. onSave renvoie une promesse :
// un échec garde la saisie et affiche un message.

function SourceTile({ icon, label, onClick })
{
	return (
		<button
			type="button"
			className="field-press"
			onClick={onClick}
			style={{ backgroundImage: 'none', backgroundColor: '#F2F3F7', minWidth: 0, minHeight: 120, borderRadius: 28, border: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 12, color: colors.text, fontSize: 17, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}
		>
			{icon}
			{label}
		</button>
	)
}

// Modifier ou ajouter la description d'une photo déjà déposée, sur place (comme un commentaire, décision 31)
function DescriptionEdit({ photo, index, onCancel, onSave })
{
	const [draft, setDraft] = useState(photo.description || '')
	const fieldRef = useRef(null)
	const fieldId = photo.id + '-edit-description'

	useEffect(() => {
		if (fieldRef.current)
			fieldRef.current.focus()
	}, [])

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
			<label htmlFor={fieldId} style={{ margin: 0, padding: '0 4px', fontSize: 15, fontWeight: 700, lineHeight: 1.3, color: colors.textSecondary }}>{'Description de la photo ' + (index + 1)}</label>
			<input
				id={fieldId}
				ref={fieldRef}
				type="text"
				className="browser-default"
				value={draft}
				placeholder="Ce que montre la photo"
				onChange={(e) => setDraft(e.target.value)}
				style={{ width: '100%', height: 52, margin: 0, padding: '0 14px', borderRadius: 12, border: '2px solid ' + colors.primary, fontSize: 17, fontFamily: 'inherit', color: colors.text, background: colors.surface, boxSizing: 'border-box' }}
			/>
			<div style={{ display: 'flex', gap: 12 }}>
				<SecondaryButton onClick={onCancel}>Annuler</SecondaryButton>
				<PrimaryButton icon={<CheckIcon size={22} />} onClick={() => onSave(draft.trim())}>Enregistrer</PrimaryButton>
			</div>
		</div>
	)
}

// Photo en grand, bouton « Retirer » posé dessus, description en dessous (canvas W19b).
// Sans onDescribe ni onRemove : lecture seule (photos déjà enregistrées). onActions : « ⋯ » sur une photo
// déjà déposée par la personne connectée (modifier la description, supprimer).
// Chaque photo et sa description forment une carte (retour d'Alexandra du 24/10/2026) : on voit à quelle
// photo le champ appartient. flex none : la liste du volet ne doit pas écraser les cartes.
function PhotoEditor({ photo, index, themeName, onDescribe, onRemove, onActions, editing, onEditCancel, onEditSave })
{
	const fieldId = photo.id + '-description'
	return (
		<div style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 10, padding: 12, borderRadius: 20, background: colors.background, border: '1px solid ' + colors.separator }}>
			<div style={{ position: 'relative' }}>
				<img src={photo.url} alt={photo.description || 'Photo ' + (index + 1)} style={{ display: 'block', width: '100%', height: 200, objectFit: 'cover', borderRadius: 14, background: '#DDE1EA' }} />
				{onRemove && (
					<button
						type="button"
						aria-label={'Retirer la photo ' + (index + 1)}
						onClick={onRemove}
						style={{ backgroundImage: 'none', backgroundColor: colors.surface, position: 'absolute', top: 8, right: 8, width: 48, height: 48, borderRadius: 999, border: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.text, boxShadow: '0 2px 10px rgba(11,16,51,.2)', cursor: 'pointer' }}
					>
						<ClearIcon />
					</button>
				)}
				{onActions && !editing && (
					<button
						type="button"
						aria-label={'Actions sur ma photo ' + (index + 1)}
						onClick={onActions}
						style={{ backgroundImage: 'none', backgroundColor: colors.surface, position: 'absolute', top: 8, right: 8, width: 48, height: 48, borderRadius: 999, border: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.text, boxShadow: '0 2px 10px rgba(11,16,51,.2)', cursor: 'pointer' }}
					>
						<DotsIcon />
					</button>
				)}
			</div>
			{themeName && <span style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', minHeight: 30, padding: '0 10px', borderRadius: 999, background: '#E8EBF5', color: colors.text, fontSize: 15, fontWeight: 600 }}>{themeName}</span>}
			{!onDescribe && !editing && <p style={{ margin: 0, padding: '0 4px', fontSize: 17, fontWeight: 500, color: photo.description ? colors.text : colors.textSecondary }}>{photo.description || 'Sans description'}</p>}
			{editing && <DescriptionEdit photo={photo} index={index} onCancel={onEditCancel} onSave={onEditSave} />}
			{onDescribe && (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
					<label htmlFor={fieldId} style={{ margin: 0, padding: '0 4px', fontSize: 15, fontWeight: 700, lineHeight: 1.3, color: colors.textSecondary }}>{'Description de la photo ' + (index + 1) + ' (facultative)'}</label>
					<input
					id={fieldId}
					type="text"
					className="browser-default"
					value={photo.description}
					placeholder="Ce que montre la photo"
						onChange={(e) => onDescribe(e.target.value)}
						style={{ width: '100%', height: 52, margin: 0, padding: '0 14px', borderRadius: 12, border: '2px solid ' + colors.border, fontSize: 17, fontFamily: 'inherit', color: colors.text, background: colors.surface, boxSizing: 'border-box' }}
					/>
				</div>
			)}
		</div>
	)
}

// existing : photos déjà déposées [{ photo, themeName }], affichées en tête. Avec onUpdateExisting et
// onRemoveExisting (avant l'enregistrement), on peut changer leur description ou les retirer ;
// sans, elles sont en lecture seule.
// knownPhotos : autres photos du même inventaire (les autres thèmes), pour repérer les doublons.
// onChangeExisting(photo) (après l'enregistrement, tour de terrain) : « ⋯ » sur mes photos déjà déposées
// pour modifier ou ajouter la description, ou supprimer (deletedTime), avec Annuler ; renvoie une promesse.
export function FieldPhotoSheet({ title, subtitle, saveLabel, user, existing = [], knownPhotos, singleScroll, onUpdateExisting, onRemoveExisting, onChangeExisting, onSave, onClose })
{
	const [drafts, setDrafts] = useState([])
	const [chooserOpen, setChooserOpen] = useState(false)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(false)
	const [preparing, setPreparing] = useState(false)
	const [duplicates, setDuplicates] = useState(0)
	const [actionsFor, setActionsFor] = useState(null)
	const [editingId, setEditingId] = useState(null)
	const [toast, setToast] = useState(null)
	const [changeError, setChangeError] = useState(false)
	const dismissToast = useCallback(() => setToast(null), [])
	const cameraRef = useRef(null)
	const galleryRef = useRef(null)
	const draftsRef = useRef(drafts)
	draftsRef.current = drafts
	const empty = drafts.length === 0
	const hasPhotos = existing.length > 0 || !empty

	// Une photo déjà présente ici (déjà déposée, ou déjà dans cette sélection) n'est pas reprise
	const addFiles = (fileList) => {
		setDuplicates(0)
		setPreparing(true)
		const known = existing.map(item => item.photo).concat(knownPhotos || [])
		preparePhotos(fileList, user, () => known.concat(draftsRef.current))
			.then(result => {
				setPreparing(false)
				setDuplicates(result.duplicates)
				if (result.photos.length > 0)
					setDrafts(current => current.concat(result.photos))
			})
	}

	const pick = (inputRef) => {
		setChooserOpen(false)
		if (inputRef.current)
			inputRef.current.click()
	}

	const change = (previous, next, text) => {
		setChangeError(false)
		Promise.resolve(onChangeExisting(next))
			.then(() => setToast({ text, undo: () => Promise.resolve(onChangeExisting(previous)).catch(() => setChangeError(true)) }))
			.catch(() => setChangeError(true))
	}
	const acting = existing.find(item => item.photo.id === actionsFor)
	const actingIndex = existing.findIndex(item => item.photo.id === actionsFor)

	const remove = (photo) => {
		URL.revokeObjectURL(photo.url)
		setDrafts(current => current.filter(item => item.id !== photo.id))
	}

	const save = () => {
		if (empty || saving)
			return ;
		setSaving(true)
		setError(false)
		Promise.resolve(onSave(drafts.map(photo => ({ ...photo, description: photo.description.trim() }))))
			.catch(() => {
				setSaving(false)
				setError(true)
			})
	}

	// Rien de nouveau mais des photos déjà déposées : on referme simplement le volet
	const footer = empty && existing.length > 0 ? <PrimaryButton onClick={onClose}>Fermer</PrimaryButton> : (
		<React.Fragment>
			{error && <ErrorNote>Les photos n'ont pas pu être enregistrées. Vérifiez la connexion, puis réessayez : elles sont gardées.</ErrorNote>}
			<PrimaryButton icon={saving || empty ? null : <CheckIcon />} disabled={empty || saving} onClick={save}>{saving ? 'Enregistrement…' : saveLabel}</PrimaryButton>
			{empty && <p style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary, textAlign: 'center' }}>Prenez ou choisissez une photo</p>}
		</React.Fragment>
	)

	return (
		<ActionSheet title={title} subtitle={subtitle} onClose={onClose} footer={footer} singleScroll={singleScroll}>
			<UndoToast toast={toast} inSheet onDismiss={dismissToast} onUndo={() => {
				toast.undo()
				setToast(null)
			}} />
			{changeError && <ErrorNote>La modification n'a pas pu être enregistrée. Vérifiez la connexion, puis réessayez.</ErrorNote>}
			{existing.map((item, index) => (
				<PhotoEditor
					key={item.photo.id}
					photo={item.photo}
					index={index}
					themeName={item.themeName}
					onDescribe={onUpdateExisting ? (description) => onUpdateExisting({ ...item.photo, description }) : null}
					onRemove={onRemoveExisting ? () => onRemoveExisting(item.photo) : null}
					onActions={onChangeExisting && isMine(item.photo.user, user) ? () => setActionsFor(item.photo.id) : null}
					editing={editingId === item.photo.id}
					onEditCancel={() => setEditingId(null)}
					onEditSave={(description) => {
						setEditingId(null)
						if (description !== (item.photo.description || ''))
							change(item.photo, { ...item.photo, description, descriptionEditTime: nowSeconds() }, 'Description modifiée')
					}}
				/>
			))}
			{acting && (
				<BottomSheet title="Ma photo" text={'Photo ' + (actingIndex + 1) + ', ' + formatDateTime(acting.photo.time)} onClose={() => setActionsFor(null)}>
					<SheetGroup>
						<SheetRow label={acting.photo.description ? 'Modifier la description' : 'Ajouter une description'} icon={<PencilIcon />} onClick={() => {
							setActionsFor(null)
							setEditingId(acting.photo.id)
						}} />
					</SheetGroup>
					<SheetGroup>
						<SheetRow label="Supprimer la photo" icon={<TrashIcon />} danger onClick={() => {
							setActionsFor(null)
							change(acting.photo, { ...acting.photo, deletedTime: nowSeconds() }, 'Photo supprimée')
						}} />
					</SheetGroup>
					<SheetGroup>
						<SheetRow label="Annuler" icon={<ClearIcon />} onClick={() => setActionsFor(null)} />
					</SheetGroup>
				</BottomSheet>
			)}
			{!hasPhotos && (
				<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
					<SourceTile icon={<CameraIcon color={colors.primary} />} label="Appareil photo" onClick={() => pick(cameraRef)} />
					<SourceTile icon={<GalleryIcon color={colors.primary} />} label="Galerie" onClick={() => pick(galleryRef)} />
				</div>
			)}
			{drafts.map((photo, index) => (
				<PhotoEditor
					key={photo.id}
					photo={photo}
					index={existing.length + index}
					onDescribe={(description) => setDrafts(current => current.map(item => (item.id === photo.id ? { ...item, description } : item)))}
					onRemove={() => remove(photo)}
				/>
			))}
			{preparing && <p role="status" style={{ flex: 'none', margin: 0, fontSize: 17, fontWeight: 600, color: colors.textSecondary }}>Préparation des photos…</p>}
			<DuplicateNote count={duplicates} />
			{hasPhotos && (
				<button
					type="button"
					onClick={() => setChooserOpen(true)}
					style={{ backgroundImage: 'none', backgroundColor: 'transparent', alignSelf: 'flex-start', minHeight: 52, display: 'inline-flex', alignItems: 'center', gap: 10, padding: 0, border: 0, color: colors.primary, fontSize: 17, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}
				>
					<CameraIcon color={colors.primary} />
					{empty ? 'Ajouter une photo' : 'Ajouter une autre photo'}
				</button>
			)}
			<input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => {
				addFiles(e.target.files)
				e.target.value = ''
			}} />
			<input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={(e) => {
				addFiles(e.target.files)
				e.target.value = ''
			}} />
			{chooserOpen && (
				<BottomSheet title="Ajouter une photo" onClose={() => setChooserOpen(false)}>
					<SheetGroup>
						<SheetRow label="Prendre une photo" icon={<CameraIcon color={colors.primary} />} onClick={() => pick(cameraRef)} />
						<SheetRow label="Choisir dans la galerie" icon={<GalleryIcon color={colors.primary} />} onClick={() => pick(galleryRef)} />
					</SheetGroup>
					<SheetGroup>
						<SheetRow label="Annuler" icon={<ClearIcon />} onClick={() => setChooserOpen(false)} />
					</SheetGroup>
				</BottomSheet>
			)}
		</ActionSheet>
	)
}

// items : commentaires déjà saisis [{ comment, themeName }] ; onUpdate(commentModifié) renvoie une promesse
export function FieldCommentSheet({ title, subtitle, listTitle, items, user, now, placeholder, saveLabel, singleScroll, onSave, onUpdate, onClose })
{
	const [draft, setDraft] = useState('')
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)
	const [toast, setToast] = useState(null)
	const dismissToast = useCallback(() => setToast(null), [])
	const empty = draft.trim() === ''

	const update = (comment) => {
		setError(null)
		Promise.resolve(onUpdate(comment)).catch(() => setError('La modification n\'a pas pu être enregistrée. Vérifiez la connexion, puis réessayez.'))
	}

	const save = () => {
		if (empty || saving)
			return ;
		setSaving(true)
		setError(null)
		Promise.resolve(onSave(draft.trim()))
			.catch(() => {
				setSaving(false)
				setError('Le commentaire n\'a pas pu être enregistré. Vérifiez la connexion, puis réessayez : votre texte est gardé.')
			})
	}

	const footer = (
		<React.Fragment>
			{error && <ErrorNote>{error}</ErrorNote>}
			<PrimaryButton icon={saving || empty ? null : <CheckIcon />} disabled={empty || saving} onClick={save}>{saving ? 'Enregistrement…' : saveLabel}</PrimaryButton>
			{empty && !saving && <p style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary, textAlign: 'center' }}>Écrivez un commentaire pour l'enregistrer.</p>}
		</React.Fragment>
	)

	return (
		<ActionSheet title={title} subtitle={subtitle} onClose={onClose} footer={footer} singleScroll={singleScroll}>
			<UndoToast
				toast={toast}
				inSheet
				onDismiss={dismissToast}
				onUndo={() => {
					toast.undo()
					setToast(null)
				}}
			/>
			{/* Deux blocs nets (retour d'Alexandra du 24/10/2026) : le titre collé à sa liste, l'intitulé collé
			    à son champ, et un vrai espace seulement entre les deux */}
			{items.length > 0 && (
				<div style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
					<FieldComments title={listTitle} comments={items} user={user} now={now} onChange={update} onToast={setToast} />
				</div>
			)}
			<div style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 8, paddingTop: items.length > 0 ? 8 : 0 }}>
			<label htmlFor="field-sheet-comment" style={{ margin: 0, fontSize: 17, fontWeight: 700, lineHeight: 1.3, color: colors.text }}>Ajouter un commentaire</label>
			<textarea
				id="field-sheet-comment"
				rows={4}
				className="browser-default"
				value={draft}
				placeholder={placeholder}
				onChange={(e) => setDraft(e.target.value)}
				style={{ display: 'block', width: '100%', minHeight: 120, margin: 0, padding: '14px 16px', borderRadius: 18, border: '2px solid ' + colors.border, background: colors.surface, fontSize: 17, lineHeight: 1.45, fontFamily: 'inherit', color: colors.text, resize: 'vertical', boxSizing: 'border-box', flex: 'none' }}
			/>
			</div>
		</ActionSheet>
	)
}
